// RoomCard — Hotel room type card with optional BEST VALUE badge.
// Reserve CTA → notYetWiredToast (hotels BE doesn't exist).
// TODO(hotels-API-PR): Wire to real room selection / checkout flow.

import { BedDouble, Check } from 'lucide-react';
import { notYetWiredToast } from './primitives';
import type { RoomOption } from '@/lib/hotels-mock';
import { useIsMobile } from '@/hooks/useIsMobile';

const T = {
  bg: '#FFF5EE', surface: '#FFFFFF', ink: '#1A0F08', inkDim: '#7A6657',
  primary: '#FF5A3C', line: 'rgba(26,15,8,0.08)', soft: '#FFE3D2',
  sans: '"Inter Variable","Inter",system-ui,sans-serif',
  display: '"Fraunces Variable","Fraunces",Georgia,serif',
};

interface RoomCardProps {
  room: RoomOption;
}

export function RoomCard({ room: r }: RoomCardProps) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '120px 1fr auto',
      gap: 16,
      padding: 16,
      borderRadius: 22,
      background: T.surface,
      border: `1.5px solid ${r.bestValue ? T.primary : T.line}`,
      position: 'relative',
      alignItems: 'center',
    }}>
      {/* BEST VALUE badge */}
      {r.bestValue && (
        <div style={{
          position: 'absolute', top: -10, left: 16,
          padding: '3px 10px', borderRadius: 999,
          background: T.primary, color: '#fff',
          fontFamily: T.sans, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
        }}>BEST VALUE</div>
      )}

      {/* Room photo */}
      <div style={{
        width: 120, height: 90, borderRadius: 16, flexShrink: 0,
        overflow: 'hidden', background: '#f0ebe3',
      }}>
        <img
          src={r.img}
          alt={r.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          loading="lazy"
        />
      </div>

      {/* Room info */}
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
        }}>
          <BedDouble size={14} stroke={T.inkDim} strokeWidth={1.8}/>
          <span style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim }}>{r.beds}</span>
        </div>
        <div style={{
          fontFamily: T.display, fontSize: 17, fontWeight: 500, color: T.ink,
          lineHeight: 1.1,
        }}>{r.name}</div>
        <div style={{
          fontFamily: T.sans, fontSize: 12, color: T.inkDim, marginTop: 3,
        }}>{r.size} · {r.view}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
          {r.perks.map(p => (
            <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Check size={11} stroke="#1F8A5B" strokeWidth={2.5}/>
              <span style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim }}>{p}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Price + CTA */}
      <div style={{ textAlign: 'right', minWidth: 110 }}>
        <div style={{
          fontFamily: T.display, fontSize: 22, fontWeight: 600, color: T.ink,
          letterSpacing: -0.01,
        }}>₹{(r.price * 83).toLocaleString('en-IN')}</div>
        <div style={{ fontFamily: T.sans, fontSize: 10, color: T.inkDim, marginBottom: 10 }}>per night</div>
        <button
          onClick={() => notYetWiredToast('Hotels')}
          style={{
            height: 36, padding: '0 16px', borderRadius: 999,
            background: T.ink, color: '#FFF5EE', border: 'none',
            fontFamily: T.sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            transition: 'opacity 200ms ease',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
        >
          select
        </button>
      </div>
    </div>
  );
}
